import { useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  FileText,
  BarChart3,
  Globe,
  MessageSquare,
  Briefcase,
  Languages,
  Image,
  Search,
  Lock,
  Mail,
  Activity,
  PenTool,
  Brain,
  Building2,
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  group?: string;
}

const navigation: NavItem[] = [
  // Ana
  { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard size={20} />, group: 'Ana' },
  { label: 'Analitik', href: '/admin/analytics', icon: <BarChart3 size={20} />, group: 'Ana' },

  // Icerik Yonetimi
  { label: 'Blog Yonetimi', href: '/admin/blog', icon: <PenTool size={20} />, group: 'Icerik' },
  { label: 'Icerik Yonetimi', href: '/admin/content', icon: <FileText size={20} />, group: 'Icerik' },
  { label: 'Medya Kutuphanesi', href: '/admin/media', icon: <Image size={20} />, group: 'Icerik' },
  { label: 'Ceviri Yonetimi', href: '/admin/translations', icon: <Languages size={20} />, group: 'Icerik' },

  // Kullanici & Iletisim
  { label: 'Kullanicilar', href: '/admin/users', icon: <Users size={20} />, group: 'Kullanici' },
  { label: 'Proje Talepleri', href: '/admin/projects', icon: <Briefcase size={20} />, group: 'Kullanici' },
  { label: 'Iletisim Mesajlari', href: '/admin/messages', icon: <MessageSquare size={20} />, group: 'Kullanici' },

  // Sistem
  { label: 'SEO Yonetimi', href: '/admin/seo', icon: <Search size={20} />, group: 'Sistem' },
  { label: 'Guvenlik', href: '/admin/security', icon: <Lock size={20} />, group: 'Sistem' },
  { label: 'E-posta Ayarlari', href: '/admin/email', icon: <Mail size={20} />, group: 'Sistem' },
  { label: 'Aktivite Loglari', href: '/admin/activity', icon: <Activity size={20} />, group: 'Sistem' },
  { label: 'AI Yonetimi', href: '/admin/ai-management', icon: <Brain size={20} />, group: 'Sistem' },
  { label: 'Tenants', href: '/admin/tenants', icon: <Building2 size={20} />, group: 'Sistem' },
  { label: 'Ayarlar', href: '/admin/settings', icon: <Settings size={20} />, group: 'Sistem' },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, isAuthenticated, checkSession, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Check authentication on mount and periodically
  useEffect(() => {
    if (!isAuthenticated || !checkSession()) {
      navigate('/admin/login', { state: { from: location.pathname }, replace: true });
    }
  }, [isAuthenticated, checkSession, navigate, location.pathname]);

  // Check session every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (!checkSession()) {
        navigate('/admin/login', { replace: true });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [checkSession, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  if (!isAuthenticated || !session) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-bg-base">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-bg-card border-r border-border-default transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-border-default">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            {sidebarOpen ? (
              <Logo size={32} showText={true} />
            ) : (
              <Logo size={32} showText={false} />
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <Menu size={18} className="text-text-secondary" />
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]">
          {['Ana', 'Icerik', 'Kullanici', 'Sistem'].map((group) => (
            <div key={group} className="mb-4">
              {sidebarOpen && (
                <p className="px-3 py-2 text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  {group === 'Kullanici' ? 'Kullanici & Iletisim' : group}
                </p>
              )}
              {navigation
                .filter((item) => item.group === group)
                .map((item) => {
                  const isActive = location.pathname === item.href ||
                    (item.href !== '/admin/dashboard' && location.pathname.startsWith(item.href));

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                        isActive
                          ? "bg-accent/10 text-accent"
                          : "text-text-secondary hover:text-text-primary hover:bg-bg-elevated"
                      )}
                    >
                      {item.icon}
                      {sidebarOpen && (
                        <>
                          <span className="flex-1 text-sm">{item.label}</span>
                          {item.badge !== undefined && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-accent text-white">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
            </div>
          ))}
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border-default bg-bg-card">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            <Globe size={20} />
            {sidebarOpen && <span>Siteyi Gor</span>}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors mt-1"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Cikis Yap</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarOpen ? "lg:ml-64" : "lg:ml-20"
      )}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-bg-card border-b border-border-default flex items-center justify-between px-4 lg:px-6">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-bg-elevated transition-colors"
          >
            <Menu size={20} className="text-text-secondary" />
          </button>

          {/* Breadcrumb / Page Title */}
          <div className="hidden lg:block">
            <h1 className="text-lg font-semibold text-text-primary capitalize">
              {location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'Dashboard'}
            </h1>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-bg-elevated transition-colors">
              <Bell size={20} className="text-text-secondary" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500" />
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-bg-elevated transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                  {session.user.email.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-text-primary">{session.user.name}</p>
                  <p className="text-xs text-text-tertiary">{session.user.role.replace('_', ' ')}</p>
                </div>
                <ChevronDown size={16} className="text-text-tertiary" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-bg-card border border-border-default rounded-lg shadow-lg z-50 overflow-hidden">
                    <div className="p-3 border-b border-border-default">
                      <p className="font-medium text-text-primary">{session.user.email}</p>
                      <p className="text-xs text-text-tertiary capitalize">{session.user.role.replace('_', ' ')}</p>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/admin/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings size={16} />
                        Hesap Ayarlari
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <LogOut size={16} />
                        Cikis Yap
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Protected Route Wrapper
export function RequireAdminAuth({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, checkSession } = useAdminAuth();

  useEffect(() => {
    if (!isAuthenticated || !checkSession()) {
      navigate('/admin/login', { state: { from: location.pathname }, replace: true });
    }
  }, [isAuthenticated, checkSession, navigate, location.pathname]);

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
